import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private cache = new Map<string, { response: HttpResponse<any>, timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only cache GET requests for specific endpoints
    if (req.method !== 'GET' || !this.shouldCache(req.url)) {
      return next.handle(req);
    }

    // Don't cache requests with cache-control headers
    if (req.headers.has('Cache-Control')) {
      return next.handle(req);
    }

    const cacheKey = `${req.url}::${req.headers.get('Accept-Language') || 'pl'}`;
    const cachedResponse = this.cache.get(cacheKey);
    const now = Date.now();

    // Return cached response if it exists and is still valid
    if (cachedResponse && (now - cachedResponse.timestamp) < this.CACHE_DURATION) {
      return of(cachedResponse.response.clone());
    }

    // Clean up expired cache entries periodically
    this.cleanupExpiredCache(now);

    // Make request and cache the response
    return next.handle(req).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          this.cache.set(cacheKey, {
            response: event.clone(),
            timestamp: now
          });
        }
      })
    );
  }

  private shouldCache(url: string): boolean {
    // Only cache specific read-only endpoints
    return url.includes('/navigation-tabs/active') || 
           url.includes('/hero-banners') ||
           url.includes('/products/categories') ||
           url.includes('/settings/');
  }

  private cleanupExpiredCache(now: number): void {
    // Only cleanup occasionally to avoid performance impact
    if (Math.random() < 0.1) { // 10% chance
      this.cache.forEach((value, key) => {
        if ((now - value.timestamp) >= this.CACHE_DURATION) {
          this.cache.delete(key);
        }
      });
    }
  }

  clearCache(): void {
    this.cache.clear();
  }

  removeCacheEntry(url: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(`${url}::`)) {
        this.cache.delete(key);
      }
    }
  }
}
